import React, { useState, useCallback } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  IconButton, 
  Box,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { 
  Delete, 
  Slideshow, 
  PlayArrow,
  NavigateNext,
  NavigateBefore,
  MoreVert,
  Label,
} from '@mui/icons-material';
import { Project, ProjectWithSlideCount, Tag } from '../../types';
import { Link as RouterLink } from 'react-router-dom';
import EmptyState from '../common/EmptyState';
import { useLang } from '../../contexts/langContext';
import ManageTagsDialog from '../common/ManageTagsDialog';

interface ProjectGridProps {
  projects: ProjectWithSlideCount[];
  onDeleteProject: (id: string) => void;
  onProjectUpdated?: (id: string, updates: Partial<ProjectWithSlideCount>) => void;
}

function ProjectCard({ 
  project, 
  onDeleteProject,
  onProjectUpdated,
}: { 
  project: ProjectWithSlideCount; 
  onDeleteProject: (id: string) => void;
  onProjectUpdated?: (id: string, updates: Partial<ProjectWithSlideCount>) => void;
}) {
  const { lang } = useLang();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);

  const slides = project.slides || [];
  const maxSlides = slides.length;

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handlePrevSlide = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTransitioning || maxSlides <= 1) return;
    
    setIsTransitioning(true);
    setCurrentSlideIndex(prev => (prev > 0 ? prev - 1 : maxSlides - 1));
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 200);
  }, [maxSlides, isTransitioning]);

  const handleNextSlide = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTransitioning || maxSlides <= 1) return;
    
    setIsTransitioning(true);
    setCurrentSlideIndex(prev => (prev + 1) % maxSlides);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 200);
  }, [maxSlides, isTransitioning]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };



  const handleDeleteClick = () => {
    onDeleteProject(project._id);
    handleMenuClose();
  };

  const handleManageTagsClick = () => {
    setManageTagsDialogOpen(true);
    handleMenuClose();
  };

  const handleTagsSaved = (newTagIds: string[]) => {
    if (onProjectUpdated) {
      onProjectUpdated(project._id, { tagIds: newTagIds });
    }
  };

  const currentSlide = slides[currentSlideIndex];
  const thumbnailUrl = currentSlide 
    ? `/api/files/thumbnails/${project.pptxFileId}/${currentSlide.pptxSlideNumber}.jpg`
    : '/images/project-placeholder.png';

  return (
    <>
      <Card
        component={RouterLink}
        to={`/projects/${project._id}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        elevation={isHovered ? 4 : 1}
        sx={{
          transition: 'all 0.2s ease-in-out',
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            pt: '56.25%', // 16:9 aspect ratio
            bgcolor: 'grey.100',
          }}
        >
          {isLoading && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          )}
          <CardMedia
            component="img"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.15s ease-in-out',
              opacity: isLoading || isTransitioning ? 0 : 1,
            }}
            image={thumbnailUrl}
            onLoad={handleImageLoad}
            onError={handleImageLoad}
            alt={project.title}
          />
          {isHovered && maxSlides > 1 && (
            <>
              <IconButton
                size="large"
                onClick={handlePrevSlide}
                disabled={isTransitioning}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                  },
                }}
              >
                <NavigateBefore />
              </IconButton>
              <IconButton
                size="large"
                onClick={handleNextSlide}
                disabled={isTransitioning}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                  },
                }}
              >
                <NavigateNext />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  backdropFilter: 'blur(4px)',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption">
                  {currentSlideIndex + 1} / {maxSlides}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom>
            {project.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {project.description}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {new Date(project.createdAt).toLocaleDateString()}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
              {project.tags && project.tags.map((tag) => (
                <Chip
                  key={tag._id}
                  label={tag.name}
                  size="small"
                  sx={{ height: 20, fontSize: '12px' }}
                />
              ))}
            </Box>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ ml: 1 }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleManageTagsClick}>
          <ListItemIcon>
            <Label fontSize="small" />
          </ListItemIcon>
          {lang === 'en' ? 'Manage Tags' : '标签'}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          {lang === 'en' ? 'Delete' : '删除'}
        </MenuItem>
      </Menu>

      <ManageTagsDialog
        open={manageTagsDialogOpen}
        onClose={() => setManageTagsDialogOpen(false)}
        entityType="project"
        entity={project}
        onSaved={handleTagsSaved}
      />
    </>
  );
}

function ProjectGrid({ projects, onDeleteProject, onProjectUpdated }: ProjectGridProps) {
  const { lang } = useLang();
  
  if (projects.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      {projects.map(project => (
        <Grid item xs={12} sm={6} md={4} key={project._id}>
          <ProjectCard
            project={project}
            onDeleteProject={onDeleteProject}
            onProjectUpdated={onProjectUpdated}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export default ProjectGrid;
